import React, {useContext, useEffect, useState} from 'react';
import {Badge, Button, Form, InputNumber, Popconfirm, Select, Space, Table, Tabs, Tag} from 'antd';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {internalApi} from '../../../../common/axios';
import toast from 'react-hot-toast';
import IsLoading from '../../../../shared/components/IsLoading';

const PUZZLE_SLOTS = Array.from({length: 12}, (_, i) => i + 1);

const CharacterCafePage = () => {
    const {playerData} = useContext(PlayerContext);
    const [cafeData, setCafeData] = useState(null);
    const [cafeList, setCafeList] = useState([]);
    const [selectedCafeId, setSelectedCafeId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [walletValues, setWalletValues] = useState({});
    const [savingBasic, setSavingBasic] = useState(false);
    const [puzzleSelections, setPuzzleSelections] = useState({});

    useEffect(() => {
        if (!playerData?.userKey) return;
        initWalletValues();
        fetchCafeData(null);
    }, [playerData?.userKey]);

    const initWalletValues = () => {
        const w = playerData?.playerInfo?.walletData ?? {};
        setWalletValues({
            CharacterCafePuzzlePiece: w.CharacterCafePuzzlePiece ?? 0,
            CharacterCafeSpecialPuzzlePiece: w.CharacterCafeSpecialPuzzlePiece ?? 0,
            CharacterCafeEventCurrency: w.CharacterCafeEventCurrency ?? 0,
            CharacterCafePoint: w.CharacterCafePoint ?? 0,
        });
    };

    const fetchCafeData = async (cafeId) => {
        setLoading(true);
        try {
            const params = {userKey: playerData.userKey};
            if (cafeId !== null) params.cafeId = cafeId;
            const {data} = await internalApi.get('/player/cafe', {params});
            setCafeData(data);
            setCafeList(data.cafeList || []);
            if (data.cafeInfo) setSelectedCafeId(data.cafeInfo.cafeId);
            const initSelections = {};
            (data.puzzles || []).forEach(p => { initSelections[p.puzzleId] = []; });
            setPuzzleSelections(initSelections);
        } catch {
            toast.error('카페 데이터 로드 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleSeasonChange = (cafeId) => {
        setSelectedCafeId(cafeId);
        fetchCafeData(cafeId);
    };

    const handleSaveBasic = async () => {
        setSavingBasic(true);
        try {
            await internalApi.post('/player/playInfo', {
                playInfo: {
                    ...playerData.playerInfo,
                    walletData: {...playerData.playerInfo.walletData, ...walletValues},
                },
                userKey: playerData.userKey,
                syncId: playerData.syncId,
            });
            toast.success('기본 정보가 저장되었습니다.');
        } catch {
            toast.error('저장 실패');
        } finally {
            setSavingBasic(false);
        }
    };

    const handleStepComplete = async (step) => {
        try {
            await internalApi.put('/player/cafe/step', {
                userKey: playerData.userKey,
                cafeId: cafeData.cafeInfo.cafeId,
                step,
            });
            toast.success('완료 처리되었습니다.');
            await fetchCafeData(selectedCafeId);
        } catch {
            toast.error('처리 실패');
        }
    };

    const handlePuzzleOpen = async (puzzleId) => {
        const addList = puzzleSelections[puzzleId] || [];
        if (!addList.length) {
            toast.error('활성화할 번호를 선택하세요.');
            return;
        }
        try {
            await internalApi.put('/player/cafe/puzzle', {
                userKey: playerData.userKey,
                cafeId: cafeData.cafeInfo.cafeId,
                puzzleId,
                addList,
            });
            toast.success('퍼즐이 활성화되었습니다.');
            setPuzzleSelections(prev => ({...prev, [puzzleId]: []}));
            await fetchCafeData(selectedCafeId);
        } catch {
            toast.error('처리 실패');
        }
    };

    const handleMissionComplete = async (missionId) => {
        try {
            await internalApi.put('/player/cafe/mission', {
                userKey: playerData.userKey,
                cafeId: cafeData.cafeInfo.cafeId,
                missionId,
            });
            toast.success('미션이 완료 처리되었습니다.');
            await fetchCafeData(selectedCafeId);
        } catch {
            toast.error('처리 실패');
        }
    };

    const togglePuzzleSlot = (puzzleId, n, opened) => {
        if (opened.has(n)) return;
        setPuzzleSelections(prev => {
            const cur = new Set(prev[puzzleId] || []);
            if (cur.has(n)) cur.delete(n);
            else cur.add(n);
            return {...prev, [puzzleId]: [...cur]};
        });
    };

    if (loading || !cafeData) return <IsLoading/>;

    const {cafeInfo, steps = [], puzzles = [], missions = []} = cafeData;

    const stepColumns = [
        {title: 'Step', dataIndex: 'step', key: 'step', width: 80},
        {title: '남은 진행', dataIndex: 'progress', key: 'progress', width: 100},
        {title: '포인트', dataIndex: 'pointValue', key: 'pointValue', width: 100},
        {
            title: '이스터에그', dataIndex: 'easterEggState', key: 'easterEggState', width: 110,
            render: v => (['없음', '활성', '수령'])[v] ?? v,
        },
        {
            title: '완료 여부', dataIndex: 'isComplete', key: 'isComplete', width: 110,
            render: v => v
                ? <Badge status="success" text="완료"/>
                : <Badge status="processing" text="진행중"/>,
        },
        {
            title: '완료 처리', key: 'action', width: 100,
            render: (_, row) => row.isComplete ? null : (
                <Popconfirm
                    title="완료 처리하시겠습니까?"
                    onConfirm={() => handleStepComplete(row.step)}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" type="primary">완료</Button>
                </Popconfirm>
            ),
        },
    ];

    const missionColumns = [
        {title: 'Mission ID', dataIndex: 'missionId', key: 'missionId', width: 100},
        {title: '미션명', dataIndex: 'missionName', key: 'missionName'},
        {title: '진행도', dataIndex: 'progress', key: 'progress', width: 80},
        {
            title: '완료', dataIndex: 'isComplete', key: 'isComplete', width: 90,
            render: v => v
                ? <Badge status="success" text="완료"/>
                : <Badge status="processing" text="진행중"/>,
        },
        {
            title: '보상 수령', dataIndex: 'isRewardClaim', key: 'isRewardClaim', width: 90,
            render: v => v ? <Tag color="green">수령</Tag> : <Tag>미수령</Tag>,
        },
        {
            title: '완료 처리', key: 'action', width: 100,
            render: (_, row) => row.isComplete ? null : (
                <Popconfirm
                    title="완료 처리하시겠습니까?"
                    onConfirm={() => handleMissionComplete(row.missionId)}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" type="primary">완료</Button>
                </Popconfirm>
            ),
        },
    ];

    const seasonOptions = cafeList.map(c => ({
        value: c.cafeId,
        label: `시즌 ${c.cafeId}${c.isSettled === 0 ? ' (진행중)' : ''}`,
    }));

    return (
        <div style={{maxWidth: 1100, margin: '0 auto', padding: 24}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20}}>
                <h2 style={{margin: 0}}>캐릭터 카페</h2>
                {cafeList.length > 0 && (
                    <Select
                        value={selectedCafeId}
                        options={seasonOptions}
                        onChange={handleSeasonChange}
                        style={{width: 180}}
                    />
                )}
            </div>

            {!cafeInfo ? (
                <div style={{padding: 24, textAlign: 'center', color: '#999'}}>
                    캐릭터 카페 이벤트 정보가 없습니다.
                </div>
            ) : (
                <>
                    <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                        <h3 style={{marginBottom: 16}}>기본 정보 조회 및 편집</h3>
                        <Space wrap size="large" align="end">
                            <Form.Item label="일반 퍼즐 조각" style={{marginBottom: 0}}>
                                <InputNumber
                                    value={walletValues.CharacterCafePuzzlePiece}
                                    onChange={v => setWalletValues(p => ({...p, CharacterCafePuzzlePiece: v}))}
                                    min={0} style={{width: 120}}
                                />
                            </Form.Item>
                            <Form.Item label="스페셜 퍼즐 조각" style={{marginBottom: 0}}>
                                <InputNumber
                                    value={walletValues.CharacterCafeSpecialPuzzlePiece}
                                    onChange={v => setWalletValues(p => ({...p, CharacterCafeSpecialPuzzlePiece: v}))}
                                    min={0} style={{width: 120}}
                                />
                            </Form.Item>
                            <Form.Item label="이벤트 재화" style={{marginBottom: 0}}>
                                <InputNumber
                                    value={walletValues.CharacterCafeEventCurrency}
                                    onChange={v => setWalletValues(p => ({...p, CharacterCafeEventCurrency: v}))}
                                    min={0} style={{width: 120}}
                                />
                            </Form.Item>
                            <Form.Item label="카페 포인트" style={{marginBottom: 0}}>
                                <InputNumber
                                    value={walletValues.CharacterCafePoint}
                                    onChange={v => setWalletValues(p => ({...p, CharacterCafePoint: v}))}
                                    min={0} style={{width: 120}}
                                />
                            </Form.Item>
                            <Button type="primary" onClick={handleSaveBasic} loading={savingBasic}>저장</Button>
                        </Space>
                    </div>

                    <Tabs defaultActiveKey="main">
                        <Tabs.TabPane tab="메인" key="main">
                            <Table
                                dataSource={steps}
                                columns={stepColumns}
                                rowKey="step"
                                pagination={false}
                                size="small"
                            />
                        </Tabs.TabPane>

                        <Tabs.TabPane tab="퍼즐 게임" key="puzzle">
                            {puzzles.length === 0 ? (
                                <div style={{textAlign: 'center', color: '#999', padding: 24}}>퍼즐 게임 데이터가 없습니다.</div>
                            ) : (
                                puzzles.map(record => {
                                    const opened = new Set(record.openedPuzzleList);
                                    const selectedNums = new Set(puzzleSelections[record.puzzleId] || []);
                                    return (
                                        <div key={record.puzzleId} style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                                            <div style={{marginBottom: 12, fontWeight: 600}}>
                                                퍼즐 ID: {record.puzzleId}&nbsp;&nbsp;
                                                <Tag color={opened.size === 12 ? 'green' : 'blue'}>
                                                    {opened.size} / 12 활성화
                                                </Tag>
                                            </div>
                                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gap: 8, marginBottom: 12}}>
                                                {PUZZLE_SLOTS.map(n => {
                                                    const isOpened = opened.has(n);
                                                    const isSelected = selectedNums.has(n);
                                                    return (
                                                        <div
                                                            key={n}
                                                            onClick={() => togglePuzzleSlot(record.puzzleId, n, opened)}
                                                            style={{
                                                                height: 64,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: isOpened ? '#52c41a' : isSelected ? '#1677ff' : '#ffffff',
                                                                color: (isOpened || isSelected) ? '#ffffff' : '#555',
                                                                border: `2px solid ${isOpened ? '#389e0d' : isSelected ? '#0958d9' : '#d9d9d9'}`,
                                                                borderRadius: 8,
                                                                cursor: isOpened ? 'default' : 'pointer',
                                                                fontWeight: 700,
                                                                fontSize: 20,
                                                                userSelect: 'none',
                                                                transition: 'background 0.15s, border-color 0.15s',
                                                            }}
                                                        >
                                                            {n}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <Popconfirm
                                                title={`선택한 ${selectedNums.size}개 슬롯을 활성화하시겠습니까?`}
                                                onConfirm={() => handlePuzzleOpen(record.puzzleId)}
                                                okText="확인"
                                                cancelText="취소"
                                                disabled={selectedNums.size === 0}
                                            >
                                                <Button type="primary" size="small" disabled={selectedNums.size === 0}>
                                                    저장 ({selectedNums.size}개 선택)
                                                </Button>
                                            </Popconfirm>
                                        </div>
                                    );
                                })
                            )}
                        </Tabs.TabPane>

                        <Tabs.TabPane tab="미션 달성" key="mission">
                            <Table
                                dataSource={missions}
                                columns={missionColumns}
                                rowKey="missionId"
                                pagination={false}
                                size="small"
                            />
                        </Tabs.TabPane>
                    </Tabs>
                </>
            )}
        </div>
    );
};

export default CharacterCafePage;
